import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { supabase } from '@/integrations/supabase/client';
import { isNative } from '@/lib/platform';
import { captureReferralFromUrl, recoverReferralCode, saveReferralCode } from '@/lib/referralStorage';

export function useAppLifecycle() {
  const navigate = useNavigate();
  const clerk = useClerk();

  useEffect(() => {
    if (!isNative) return;
    // Some native builds ship without the @capacitor/app plugin registered —
    // calling addListener there throws ("not implemented on android").
    if (!Capacitor.isPluginAvailable('App')) return;

    let stateSub: Promise<{ remove: () => void }> | undefined;
    let urlSub: Promise<{ remove: () => void }> | undefined;

    try {
    // Refresh FPL session on foreground + recover referral code from Preferences
    stateSub = App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        await recoverReferralCode();
        // FPL session refresh is now scheduled server-side (cron) and is admin/cron-only.
      }
    });

    // Handle deep links (referral codes, auth callbacks)
    urlSub = App.addListener('appUrlOpen', async ({ url }) => {
      // Close in-app browser first (for OAuth flow)
      try {
        await Browser.close();
      } catch {
        // Browser may not be open
      }

      // Handle Clerk auth callback
      if (url.includes('fantasyfootballafrica://callback')) {
        const params = new URLSearchParams(url.split('?')[1] || '');

        try {
          await clerk.handleRedirectCallback({
            afterSignInUrl: '/',
            afterSignUpUrl: '/',
          });
        } catch (err) {
          console.error('Auth callback error:', err);
        }

        // Handle referral code in the same URL if present
        const ref = params.get('ref');
        if (ref && /^FPM-[A-Z0-9]{8}$/.test(ref)) {
          await saveReferralCode(ref);
        }
        return;
      }

      // Handle referral deep links
      const captured = await captureReferralFromUrl(url);
      if (captured) {
        navigate('/');
        return;
      }
    });
    } catch (err) {
      console.warn('Capacitor App listener registration failed:', err);
    }

    return () => {
      stateSub?.then(l => l.remove()).catch(() => {});
      urlSub?.then(l => l.remove()).catch(() => {});
    };
  }, [navigate, clerk]);
}
